import { api } from "../../../core/lib/api";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  hasWarrantyContact,
  isValidVin,
  isWarrantyEditable,
  maintenanceDueAt,
  type Warranty,
  type WarrantyTemplate,
} from "@shared/modules/x-garanties";
import { ApiError } from "../../../core/lib/api";
import { formatDateTime, toDate } from "../../../core/lib/format";
import { moduleEnabled } from "../../../core/blueprint";
import { useAuthStore } from "../../../core/stores/auth";
import { useToastStore } from "../../../core/stores/toast";
import { Button } from "../../../core/components/ui/button";
import { Field, Input } from "../../../core/components/ui/input";
import { Modal } from "../../../core/components/ui/modal";
import { Icon } from "../../../core/components/ui/icon";
import { BackLink } from "../../../core/components/ui/back-link";
import { WarrantyStatusBadge } from "../components/status-badge";
import { TermsMarkdown } from "../components/terms-markdown";
import {
  cancelWarranty,
  deleteWarranty,
  documentUrl,
  emitWarranty,
  getWarranty,
  listTemplates,
  requestSignatureLink,
  signWarranty,
  updateWarrantyContact,
} from "../lib/api";
import { formatDay } from "../lib/dates";

type Failure = "not_found" | "error" | null;

/** Fiche d'une garantie : statut, cycle de vie, document imprimable, rappel d'entretien. */
export function GarantiePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const push = useToastStore((s) => s.push);
  const isAdmin = useAuthStore((s) => s.user?.role === "admin");
  const atelier = moduleEnabled("x-atelier");

  const [w, setW] = useState<Warranty | null>(null);
  const [template, setTemplate] = useState<WarrantyTemplate | null>(null);
  const [failure, setFailure] = useState<Failure>(null);
  const [busy, setBusy] = useState(false);
  const [signing, setSigning] = useState(false);
  const [signedBy, setSignedBy] = useState("");
  const [signatureStatus, setSignatureStatus] = useState<{
    signature: { signed_at: number | null; requested_at: number } | null;
    automation: { status: string; error: string | null } | null;
  } | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Coordonnées en cours de modification sur une garantie dont le document est figé.
  const [contactDraft, setContactDraft] = useState<{ phone: string; email: string } | null>(null);

  const load = useCallback(() => {
    if (!id) return;
    void api<NonNullable<typeof signatureStatus>>(`/api/garanties/${id}/signature-status`)
      .then(setSignatureStatus)
      .catch(() => setSignatureStatus(null));
    getWarranty(id)
      .then((view) => {
        setW(view);
        setFailure(null);
      })
      .catch((err: unknown) => {
        setW(null);
        setFailure(err instanceof ApiError && err.status === 404 ? "not_found" : "error");
      });
    listTemplates()
      .then((list) => setTemplate(list[0] ?? null))
      .catch(() => setTemplate(null));
  }, [id]);

  useEffect(load, [load]);

  const run = async (action: () => Promise<Warranty>, success: string, failureTitle: string) => {
    if (busy) return;
    setBusy(true);
    try {
      setW(await action());
      push("success", success);
    } catch (err) {
      push("danger", failureTitle, describe(err));
    } finally {
      setBusy(false);
    }
  };

  const emit = () => {
    if (!w) return;
    if (!isValidVin(w.vin)) {
      push("warning", "NIV requis", "Ajoutez un NIV de 17 caractères avant d'émettre.");
      navigate(`/garanties/${w.id}/modifier`);
      return;
    }
    void run(() => emitWarranty(w.id), "Garantie émise", "Émission impossible");
  };

  const sign = async () => {
    if (!w || !signedBy.trim()) return;
    await run(
      () => signWarranty(w.id, signedBy.trim()),
      "Garantie marquée signée",
      "Signature impossible",
    );
    setSigning(false);
  };

  const cancel = async () => {
    if (!w) return;
    await run(() => cancelWarranty(w.id), "Garantie annulée", "Annulation impossible");
    setCancelling(false);
  };

  const remove = async () => {
    if (!w || busy) return;
    setBusy(true);
    try {
      await deleteWarranty(w.id);
      push("success", "Garantie supprimée");
      navigate("/garanties");
    } catch {
      push("danger", "Suppression impossible");
      setBusy(false);
    }
  };

  /** Lien de signature ; `renew` en émet un nouveau et l'envoie par le canal actuel :
   * courriel avec le PDF si le client en a un, sinon texto. Les liens déjà envoyés restent
   * valides (30 octobre 2026). */
  const requestLink = async (renew: boolean) => {
    if (!w || busy) return;
    setBusy(true);
    try {
      const r = await requestSignatureLink(w.id, renew);
      if (r.status === "deja_envoyee") {
        // Le message est vraiment parti (sinon l'API l'aurait renvoyé) : dire quand et comment.
        const quand = r.sentAt ? formatDateTime(r.sentAt, { dateStyle: "long" }) : null;
        const canal =
          r.channel === "sms" ? "par texto" : r.channel === "email" ? "par courriel" : "";
        push(
          "success",
          quand ? `Lien déjà envoyé ${canal} le ${quand}` : "Lien déjà préparé",
          "Détail dans Messages. Pour l’envoyer de nouveau : « Renvoyer le lien de signature ».",
        );
      } else if (r.status === "signee") {
        push("warning", "Cette garantie est déjà signée");
      } else {
        const canal =
          r.channel === "email"
            ? "Par courriel, avec le certificat PDF."
            : "Par texto, avec le lien : aucun courriel au dossier.";
        push(
          "success",
          renew ? "Nouveau lien de signature envoyé" : "Demande de signature préparée",
          renew ? `${canal} Les liens déjà envoyés restent valides.` : canal,
        );
      }
      load();
    } catch (err) {
      push(
        "danger",
        "Envoi impossible",
        describe(err) ?? "Vérifiez les coordonnées et les préférences du client",
      );
    } finally {
      setBusy(false);
    }
  };

  const saveContact = async () => {
    if (!w || !contactDraft || busy) return;
    const clientPhone = contactDraft.phone.trim() || null;
    const clientEmail = contactDraft.email.trim() || null;
    if (!hasWarrantyContact({ clientPhone, clientEmail })) {
      push("danger", "Téléphone ou courriel obligatoire");
      return;
    }
    setBusy(true);
    try {
      setW(await updateWarrantyContact(w.id, { clientPhone, clientEmail }));
      push(
        "success",
        "Coordonnées enregistrées",
        "Refaites le lien de signature pour l’envoyer par ce canal.",
      );
      setContactDraft(null);
    } catch (err) {
      push("danger", "Enregistrement impossible", describe(err));
    } finally {
      setBusy(false);
    }
  };

  if (failure) {
    return (
      <div className="page">
        <p className="garantie-backlink">
          <BackLink fallback="/garanties" />
        </p>
        <p className="empty">
          {failure === "not_found"
            ? "Garantie introuvable — elle a peut-être été supprimée."
            : "Fiche indisponible — réessayez dans un instant."}
        </p>
      </div>
    );
  }

  if (!w) return <p className="empty">Chargement…</p>;

  const issued = toDate(w.issuedAt);
  const maintenance =
    issued && template ? maintenanceDueAt(issued, template.maintenanceMonths) : null;
  const maintenanceOverdue = maintenance ? maintenance.getTime() < Date.now() : false;
  const editable = isWarrantyEditable(w.status) && !signatureStatus?.signature;
  // Lien préparé et pas encore signé : document figé, coordonnées encore modifiables.
  const pendingSignature = Boolean(
    signatureStatus?.signature && !signatureStatus.signature.signed_at,
  );
  const canCancel = w.status === "brouillon" || w.status === "envoyee" || w.status === "signee";

  return (
    <div className="page garantie">
      <p className="garantie-backlink">
        <BackLink fallback="/garanties" />
      </p>

      <header className="garantie__head">
        <div className="garantie__titles">
          <h1 className="garantie__title">{w.number}</h1>
          <p className="garantie__sub">
            <WarrantyStatusBadge status={w.status} />
            {atelier ? (
              <Link to={`/clients/${w.clientId}`}>{w.clientName}</Link>
            ) : (
              <span>{w.clientName}</span>
            )}
            <span>{w.vehicleLabel}</span>
          </p>
        </div>
        <div className="garantie__actions">
          {w.status === "brouillon" ? (
            <Button variant="primary" size="sm" disabled={busy} onClick={emit}>
              Émettre
            </Button>
          ) : null}
          {w.status === "envoyee" && !signatureStatus?.signature ? (
            <Button
              variant="primary"
              size="sm"
              disabled={busy}
              onClick={() => {
                setSignedBy(w.clientName);
                setSigning(true);
              }}
            >
              Signature papier reçue
            </Button>
          ) : null}
          {w.status === "envoyee" ? (
            <Button size="sm" disabled={busy} onClick={() => void requestLink(pendingSignature)}>
              {pendingSignature ? "Renvoyer le lien de signature" : "Envoyer le lien de signature"}
            </Button>
          ) : null}
          <a
            className="btn btn--ghost btn--sm"
            href={documentUrl(w.id)}
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className="btn__icon">
              <Icon name="external" size={16} />
            </span>
            Ouvrir le document
          </a>
          {editable ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/garanties/${w.id}/modifier`)}
            >
              Modifier
            </Button>
          ) : null}
          {canCancel ? (
            <Button
              variant="ghost-danger"
              size="sm"
              disabled={busy}
              onClick={() => setCancelling(true)}
            >
              Annuler
            </Button>
          ) : null}
          {isAdmin ? (
            <Button
              variant="ghost-danger"
              size="sm"
              disabled={busy}
              onClick={() => setDeleting(true)}
            >
              Supprimer
            </Button>
          ) : null}
        </div>
      </header>

      {signatureStatus?.automation?.error ? (
        <p className="garantie__notice">
          Envoi automatique à compléter : {signatureStatus.automation.error}
        </p>
      ) : null}
      {signatureStatus?.signature ? (
        <p className="garantie__notice">
          {signatureStatus.signature.signed_at
            ? "Signature client reçue et copie archivée."
            : "Document figé pour signature client. L’état de l’envoi est disponible dans Messages."}{" "}
          <a href={`/api/garanties/${w.id}/document.pdf`} target="_blank" rel="noreferrer">
            Télécharger le PDF
          </a>
        </p>
      ) : null}
      {w.status === "brouillon" && !w.vin ? (
        <p className="garantie__notice">
          NIV manquant : la garantie ne pourra pas être émise tant que le numéro d'identification du
          véhicule n'est pas saisi. <Link to={`/garanties/${w.id}/modifier`}>Ajouter le NIV</Link>
        </p>
      ) : null}

      <dl className="garantie__dates">
        <div className="garantie__date">
          <dt>Émise le</dt>
          <dd>{formatDay(w.issuedAt)}</dd>
        </div>
        <div className="garantie__date">
          <dt>Expire le</dt>
          <dd>{formatDay(w.expiresAt)}</dd>
        </div>
        <div
          className={`garantie__date garantie__date--maintenance${maintenanceOverdue ? " garantie__date--overdue" : ""}`}
        >
          <dt>Prochain entretien</dt>
          <dd>{maintenance ? formatDay(maintenance.getTime()) : "—"}</dd>
          {template ? (
            <span className="garantie__date-hint">
              Tous les {template.maintenanceMonths} mois{maintenanceOverdue ? " — en retard" : ""}
            </span>
          ) : null}
        </div>
      </dl>

      <div className="garantie__grid">
        <section className="card garantie__block">
          <h2 className="garantie__block-title">Titulaire</h2>
          <dl className="kv">
            <dt>Nom</dt>
            <dd>{w.clientName}</dd>
            <dt>Téléphone</dt>
            <dd>{w.clientPhone ?? "—"}</dd>
            <dt>Courriel</dt>
            <dd>{w.clientEmail ?? "—"}</dd>
            <dt>Adresse</dt>
            <dd>{w.clientAddress ?? "—"}</dd>
          </dl>
          {pendingSignature && isWarrantyEditable(w.status) ? (
            contactDraft ? (
              <div className="form-grid">
                <Field label="Téléphone">
                  <Input
                    value={contactDraft.phone}
                    onChange={(e) => setContactDraft({ ...contactDraft, phone: e.target.value })}
                  />
                </Field>
                <Field label="Courriel">
                  <Input
                    type="email"
                    value={contactDraft.email}
                    onChange={(e) => setContactDraft({ ...contactDraft, email: e.target.value })}
                  />
                </Field>
                <div className="form-grid__full garantie__actions">
                  <Button variant="ghost" size="sm" onClick={() => setContactDraft(null)}>
                    Annuler
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    disabled={busy}
                    onClick={() => void saveContact()}
                  >
                    Enregistrer
                  </Button>
                </div>
              </div>
            ) : (
              <p className="garantie__notice">
                Document figé pour signature : seules les coordonnées se modifient encore.
                {w.clientEmail
                  ? ""
                  : " Sans courriel, le lien part par texto, sans le certificat PDF."}{" "}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    setContactDraft({ phone: w.clientPhone ?? "", email: w.clientEmail ?? "" })
                  }
                >
                  Modifier les coordonnées
                </Button>
              </p>
            )
          ) : null}
        </section>

        <section className="card garantie__block">
          <h2 className="garantie__block-title">Véhicule</h2>
          <dl className="kv">
            <dt>Véhicule</dt>
            <dd>{w.vehicleLabel}</dd>
            <dt>NIV</dt>
            <dd>
              {w.vin ? (
                <span className="garantie__vin">{w.vin}</span>
              ) : (
                <span className="garantie__missing">Manquant</span>
              )}
            </dd>
            <dt>Kilométrage</dt>
            <dd>{w.mileage !== null ? `${w.mileage.toLocaleString("fr-CA")} km` : "—"}</dd>
            <dt>Service</dt>
            <dd>{w.serviceLabel ?? "—"}</dd>
            {w.appointmentId && atelier ? (
              <>
                <dt>Travail</dt>
                <dd>
                  <Link to={`/travaux/${w.appointmentId}`}>Ouvrir le rendez-vous</Link>
                </dd>
              </>
            ) : null}
          </dl>
        </section>

        <section className="card garantie__block">
          <h2 className="garantie__block-title">Suivi</h2>
          <dl className="kv">
            <dt>Statut</dt>
            <dd>
              <WarrantyStatusBadge status={w.status} />
            </dd>
            <dt>Signée</dt>
            <dd>
              {w.signedAt
                ? `${formatDay(w.signedAt)}${w.signedBy ? ` par ${w.signedBy}` : ""}`
                : "Non"}
            </dd>
            <dt>Créée le</dt>
            <dd>{formatDateTime(w.createdAt, { dateStyle: "medium", timeStyle: "short" })}</dd>
            <dt>Modifiée le</dt>
            <dd>{formatDateTime(w.updatedAt, { dateStyle: "medium", timeStyle: "short" })}</dd>
          </dl>
        </section>
      </div>

      <section className="card garantie__block">
        <h2 className="garantie__block-title">Termes de la garantie</h2>
        <TermsMarkdown body={w.termsMd} />
      </section>

      <section className="card garantie__block">
        <h2 className="garantie__block-title">Notes</h2>
        {w.notes ? (
          <p className="garantie__notes">{w.notes}</p>
        ) : (
          <p className="garantie__empty">Aucune note.</p>
        )}
      </section>

      <Modal
        open={signing}
        title="Marquer la garantie comme signée"
        onClose={() => setSigning(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setSigning(false)}>
              Annuler
            </Button>
            <Button
              variant="primary"
              disabled={busy || !signedBy.trim()}
              onClick={() => void sign()}
            >
              Confirmer la signature
            </Button>
          </>
        }
      >
        <div className="form-grid">
          <div className="form-grid__full">
            <Field label="Nom du signataire" hint="Tel qu'écrit sur le certificat signé.">
              <Input
                value={signedBy}
                onChange={(e) => setSignedBy(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void sign();
                }}
                autoFocus
              />
            </Field>
          </div>
        </div>
      </Modal>

      <Modal
        open={cancelling}
        title="Annuler cette garantie ?"
        onClose={() => setCancelling(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setCancelling(false)}>
              Conserver
            </Button>
            <Button variant="danger" disabled={busy} onClick={() => void cancel()}>
              Annuler la garantie
            </Button>
          </>
        }
      >
        <p>
          « {w.number} » passera au statut Annulée. Le numéro n'est pas réattribué et le document
          reste consultable.
        </p>
      </Modal>

      <Modal
        open={deleting}
        title="Supprimer cette garantie ?"
        onClose={() => setDeleting(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeleting(false)}>
              Annuler
            </Button>
            <Button variant="danger" disabled={busy} onClick={() => void remove()}>
              Supprimer définitivement
            </Button>
          </>
        }
      >
        <p>« {w.number} » sera supprimée. Préférez l'annulation pour garder une trace.</p>
      </Modal>
    </div>
  );
}

function describe(err: unknown): string | undefined {
  if (!(err instanceof ApiError)) return undefined;
  switch (err.code) {
    case "vin_required":
      return "Le NIV est obligatoire.";
    case "vin_invalid":
      return "Le NIV doit compter 17 caractères, sans I, O ni Q.";
    case "invalid_status":
      return "Le statut actuel ne permet pas cette action.";
    case "contact_required":
      return "Indiquez au moins un téléphone ou un courriel.";
    case "locked":
      return "Document figé pour signature : seules les coordonnées se modifient.";
    case "warranty_not_issued":
      return "La garantie doit être émise avant l’envoi du lien.";
    case "signature_recipient_unavailable":
      return "Aucune coordonnée utilisable, ou le client a refusé les communications.";
    default:
      return undefined;
  }
}
